// RS3 uses one gesture owner for both chapters and the product's color steps.
window.RS7GTPageScroll = class RS3GTPageScroll extends window.RS7GTPageScroll {
  colorScene(index = this.currentIndex()) {
    const product = this.stops[index]?.element.closest('rs3gt-product');
    return product?.enabled ? product : null;
  }

  advance(direction, index = this.currentIndex()) {
    const product = this.colorScene(index);
    if ((this.reserveTouchColor ? product?.reserveColor(direction) : product?.advanceColor(direction))) return;
    if (direction > 0 && index === this.stops.length - 1
      && this.stops[index]?.element.matches('.rs3-closing')
      && this.stops[0]?.element === this.root.hero) {
      this.returnToOpening();
      return;
    }
    super.advance(direction, index);
  }

  advanceWheel(direction, now, product = this.colorScene(), reserve = false) {
    const desired = product?.desiredStage;
    if (reserve && product) product.reserveColor(direction);
    else this.advance(direction);
    if (product && product.desiredStage !== desired) product.wheelIntentAt = now;
  }

  onWheel(event) {
    if (event.defaultPrevented || event.ctrlKey) return;
    if (this.loop) { super.onWheel(event); return; }
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.height : 1;
    const delta = event.deltaY * unit;
    if (this.root.chapters?.containsOpenPanel(event.target)) {
      if (!this.canScrollInside(event.target, delta)) event.preventDefault();
      return;
    }
    if (!this.root.contains(event.target)
      || event.target.closest('select, textarea, input, [contenteditable="true"], dialog')
      || Math.abs(event.deltaX) > Math.abs(event.deltaY) || !delta) return;

    const now = performance.now();
    const direction = Math.sign(delta);
    const gap = now - this.lastWheel;
    const color = this.colorScene();
    const blocked = !!this.animation || now < this.lockUntil;
    // Rebounds and late momentum packets belong to the same physical gesture.
    // A quiet interval or an explicit platform phase arms another step. When
    // trackpad inertia hides that interval, the restart detector below waits
    // for a sustained rise instead of dropping the user's next stroke.
    const explicitFresh = this.wheelMomentum && event.momentum === false;
    const fresh = gap > 300 || explicitFresh;
    // A quiet hole can occur inside a trackpad's inertial tail. Keep ownership
    // while a clip runs and briefly after it settles; only a clear new stroke
    // may reserve the next leg or leave the product at its boundary.
    const settleGuard = color?.wheelSettledAt && now - color.wheelSettledAt < 520;
    const preserveBusyGesture = !!(color?.busy || settleGuard) && this.wheelUsed && !explicitFresh;
    if (fresh && !preserveBusyGesture) {
      const pending = !this.wheelUsed && gap < 450 && direction === this.wheelDirection ? this.wheelTotal : 0;
      this.resetWheelGesture(direction);
      this.wheelTotal = pending;
      this.wheelUsed = !!blocked;
      this.wheelNativeKey = null;
    }
    this.lastWheel = now;
    this.wheelMomentum = event.momentum === true;
    const magnitude = Math.abs(delta);
    const previousPeak = this.wheelPeak;
    const previousSamples = this.wheelSamples.slice();
    this.wheelPeak = Math.max(this.wheelPeak, magnitude);
    this.wheelSamples.push(magnitude);
    if (this.wheelSamples.length > 3) this.wheelSamples.shift();
    if (blocked) {
      event.preventDefault();
      this.wheelUsed = true;
      this.wheelTotal = 0;
      this.wheelNativeKey = null;
      this.wheelCandidate = null;
      return;
    }

    const key = this.stops[this.currentIndex()]?.key;
    // A gesture that paged into a chapter cannot also move its inner gallery.
    // A gesture begun inside the gallery may keep scrolling it naturally.
    if ((!this.wheelUsed && !this.wheelMomentum || this.wheelNativeKey === key)
      && this.canScrollInside(event.target, delta)) {
      this.wheelUsed = true;
      this.wheelNativeKey = key;
      return;
    }
    event.preventDefault();
    if (this.wheelMomentum) {
      this.wheelUsed = true;
      this.wheelTotal = 0;
      this.wheelCandidate = null;
      return;
    }
    if (color?.busy) {
      const floor = previousSamples.length ? Math.max(...previousSamples.slice(-3)) : Infinity;
      const age = now - (this.wheelStepAt || 0);
      const activeDirection = color.transition?.direction
        || Math.sign(color.desiredStage - color.settledStage);
      const sameDirection = direction === activeDirection;
      const mouseSizedNotch = event.deltaMode ? true : magnitude >= 80;
      const repeatedNotch = gap > 72 && mouseSizedNotch && magnitude <= 160 && Number.isInteger(event.deltaY)
        && Number.isFinite(floor) && Math.abs(magnitude - floor) <= Math.max(2, floor * .08);
      const risingRestart = magnitude >= 48 && (
        (floor <= Math.max(20, previousPeak * .5) && magnitude >= Math.max(48, floor * 1.8))
        || magnitude >= Math.max(80, previousPeak * 1.8)
      );
      const strongRestart = sameDirection && age > 96
        && (repeatedNotch || risingRestart);
      const tailHasFallen = floor <= Math.max(8, previousPeak * .4);
      const candidate = this.wheelCandidate;
      if (candidate && candidate.direction === direction && magnitude >= candidate.minimum * .65) {
        candidate.total += delta;
        candidate.count++;
        candidate.peak = Math.max(candidate.peak, magnitude);
      } else {
        const minimum = Math.max(5, Math.min(20, floor * 1.5));
        const risingFromTail = Number.isFinite(floor) && magnitude >= Math.max(minimum, floor * 1.35);
        this.wheelCandidate = sameDirection && age > 96 && tailHasFallen && risingFromTail
          ? { direction, minimum, total: delta, count: 1, peak: magnitude } : null;
      }
      const restart = this.wheelCandidate;
      const gradualRestart = restart?.count >= 2 && Math.abs(restart.total) >= 28 && restart.peak >= 16;
      this.wheelUsed = true;
      this.wheelTotal = 0;
      if (strongRestart || gradualRestart) {
        this.wheelCandidate = null;
        this.wheelStepAt = now;
        this.advanceWheel(direction, now, color, true);
      }
      return;
    }
    if (this.wheelUsed && color && (!fresh || preserveBusyGesture)) {
      const samples = this.wheelSamples.slice(0, -1);
      const floor = samples.length ? Math.max(...samples) : Infinity;
      const age = now - (this.wheelStepAt || 0);
      const sameDirection = direction === this.wheelDirection;
      // One pronounced packet is a mouse-wheel notch or the start of a firm
      // trackpad stroke. It cannot be the small tail which follows a clip.
      const mouseSizedNotch = event.deltaMode ? true : magnitude >= 80;
      const repeatedNotch = gap > 72 && mouseSizedNotch && magnitude <= 160 && Number.isInteger(event.deltaY)
        && Number.isFinite(floor) && Math.abs(magnitude - floor) <= Math.max(2, floor * .08);
      const risingRestart = magnitude >= 48 && (
        (floor <= Math.max(20, previousPeak * .5) && magnitude >= Math.max(48, floor * 1.8))
        || magnitude >= Math.max(80, previousPeak * 1.8)
      );
      const strongRestart = sameDirection && age > 96
        && (repeatedNotch || risingRestart);
      if (strongRestart) {
        this.resetWheelGesture(direction);
        this.wheelUsed = true;
        this.wheelPeak = magnitude;
        this.wheelSamples = [magnitude];
        this.wheelStepAt = now;
        this.advanceWheel(direction, now, color);
        return;
      }

      const candidate = this.wheelCandidate;
      if (candidate && candidate.direction === direction && magnitude >= candidate.minimum * .65) {
        candidate.total += delta;
        candidate.count++;
        candidate.peak = Math.max(candidate.peak, magnitude);
      } else {
        const minimum = Math.max(5, Math.min(32, floor * 1.8));
        const tailHasFallen = floor <= Math.max(8, this.wheelPeak * .4);
        this.wheelCandidate = sameDirection && age > 320 && tailHasFallen && magnitude >= minimum
          ? { direction, minimum, total: delta, count: 1, peak: magnitude } : null;
      }
      const restart = this.wheelCandidate;
      const requiredPeak = Math.max(18, Math.min(40, this.wheelPeak * .3));
      if (restart?.count >= 2 && Math.abs(restart.total) >= 32 && restart.peak >= requiredPeak) {
        this.resetWheelGesture(direction);
        this.wheelUsed = true;
        this.wheelPeak = magnitude;
        this.wheelSamples = [magnitude];
        this.wheelStepAt = now;
        this.advanceWheel(direction, now, color);
      }
      return;
    }
    if (this.wheelUsed) return;
    if (direction !== this.wheelDirection) {
      this.wheelTotal = 0;
      this.wheelDirection = direction;
    }
    this.wheelTotal += delta;
    if (!color && Math.abs(this.wheelTotal) < 40) return;
    this.wheelUsed = true;
    this.wheelStepAt = now;
    this.advanceWheel(direction, now, color);
  }

  onTouchMove(event) {
    const color = this.touch && !this.touch.used ? this.colorScene() : null;
    this.reserveTouchColor = !!color?.busy;
    try { super.onTouchMove(event); }
    finally { this.reserveTouchColor = false; }
  }

  touchThreshold(touch) {
    const index = this.stops.findIndex(stop => stop.key === touch?.originKey);
    return this.colorScene(index >= 0 ? index : this.currentIndex()) ? 28 : super.touchThreshold(touch);
  }

  onKey(event) {
    const direction = { ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1 }[event.key];
    if (direction && !event.defaultPrevented && !event.ctrlKey && !event.metaKey && !event.altKey
      && event.target.closest?.('[data-rs3-color]') && this.colorScene()) {
      event.preventDefault();
      if (!event.repeat && !this.animation && performance.now() >= this.lockUntil) this.advance(direction);
      return;
    }
    super.onKey(event);
  }
};
